import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { getToken, errMsg } from "@/lib/api";
import DoctorShell from "@/components/DoctorShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2, Download, ShieldCheck } from "lucide-react";

const CATEGORIES = [
    { value: "registration_certificate", label: "Registration certificate" },
    { value: "degree", label: "Degree" },
    { value: "license", label: "License" },
    { value: "membership", label: "Membership" },
    { value: "award", label: "Award" },
    { value: "other", label: "Other" },
];

export default function Documents() {
    const [docs, setDocs] = useState([]);
    const [category, setCategory] = useState("degree");
    const [label, setLabel] = useState("");
    const [busy, setBusy] = useState(false);
    const [doctor, setDoctor] = useState(null);
    const fileRef = useRef(null);

    async function load() {
        try {
            const r = await api.get("/me/documents");
            setDocs(r.data);
        } catch (e) {
            toast.error(errMsg(e, "Failed to load"));
        }
    }
    useEffect(() => {
        api.get("/me/doctor").then((r) => setDoctor(r.data)).catch(() => {});
        load();
    }, []);

    async function upload() {
        const f = fileRef.current?.files?.[0];
        if (!f) return toast.error("Choose a file");
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("category", category);
            fd.append("label", label);
            fd.append("file", f);
            await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/me/documents`, {
                method: "POST",
                headers: { Authorization: `Bearer ${getToken()}` },
                body: fd,
            }).then(async (r) => {
                if (!r.ok) throw new Error((await r.json().catch(() => ({})))?.detail || r.statusText);
            });
            toast.success("Uploaded");
            setLabel("");
            fileRef.current.value = "";
            load();
        } catch (e) {
            toast.error(errMsg(e, "Upload failed"));
        } finally {
            setBusy(false);
        }
    }

    async function del(id) {
        if (!confirm("Delete this document?")) return;
        try {
            await api.delete(`/me/documents/${id}`);
            toast.success("Deleted");
            load();
        } catch (e) {
            toast.error(errMsg(e, "Failed"));
        }
    }

    async function download(id, filename) {
        try {
            const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/me/documents/${id}/download`, {
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            if (!r.ok) throw new Error("Download failed");
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = filename;
            document.body.appendChild(a); a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(errMsg(e, "Failed"));
        }
    }

    return (
        <DoctorShell doctor={doctor}>
            <div className="mb-6" data-testid="documents-header">
                <div className="text-xs uppercase tracking-widest text-[#123B5D]/60">Vault</div>
                <h1 className="text-2xl font-bold text-[#123B5D]">Documents</h1>
                <p className="text-[#17212B]/60 text-sm mt-1">
                    Private storage for your credentials. Only you (and platform admins for verification) can access these files.
                    PDF/JPG/PNG · 10 MB max per file.
                </p>
            </div>

            <Card className="border-[#123B5D]/10 mb-6">
                <CardHeader><CardTitle className="text-sm">Upload document</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                    <div>
                        <div className="text-xs text-[#17212B]/60 mb-1">Category</div>
                        <select className="w-full border border-[#123B5D]/20 rounded-md h-10 px-3 text-sm" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="doc-category">
                            {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <div className="text-xs text-[#17212B]/60 mb-1">Label (optional)</div>
                        <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. MBBS certificate" data-testid="doc-label" />
                    </div>
                    <div>
                        <div className="text-xs text-[#17212B]/60 mb-1">File</div>
                        <input ref={fileRef} type="file" accept="application/pdf,image/jpeg,image/png,image/webp" data-testid="doc-file" />
                    </div>
                    <Button onClick={upload} disabled={busy} data-testid="doc-upload">Upload</Button>
                </CardContent>
            </Card>

            <Card className="border-[#123B5D]/10">
                <CardHeader><CardTitle className="text-sm">Your documents ({docs.length})</CardTitle></CardHeader>
                <CardContent>
                    {docs.length === 0 ? (
                        <div className="text-sm text-[#17212B]/60 py-4">No documents yet.</div>
                    ) : (
                        <ul className="divide-y divide-[#123B5D]/10">
                            {docs.map((d) => (
                                <li key={d.id} className="py-3 flex items-center gap-3" data-testid={`doc-${d.id}`}>
                                    <div className="flex-1">
                                        <div className="font-semibold text-[#123B5D]">{d.label}</div>
                                        <div className="text-xs text-[#17212B]/60">
                                            {d.category} · {d.content_type} · {Math.round((d.size || 0) / 1024)} KB · {d.filename}
                                        </div>
                                    </div>
                                    {d.verified && <span className="inline-flex items-center gap-1 text-xs text-[#15803D] bg-green-50 rounded-full px-2 py-1" data-testid={`doc-verified-${d.id}`}><ShieldCheck size={12} /> Verified</span>}
                                    <Button size="sm" variant="ghost" onClick={() => download(d.id, d.filename)} data-testid={`doc-download-${d.id}`}><Download size={14} /></Button>
                                    <Button size="sm" variant="ghost" onClick={() => del(d.id)} data-testid={`doc-delete-${d.id}`}><Trash2 size={14} className="text-[#DC2626]" /></Button>
                                </li>
                            ))}
                        </ul>
                    )}
                </CardContent>
            </Card>
        </DoctorShell>
    );
}
